import { motion } from 'framer-motion';
import { Calendar, MapPin, Zap } from 'lucide-react';
import { useState } from 'react';
import projectsData from '../data/projects';
import Seo from '../components/Seo';
import { PAGES } from '../seo/meta.mjs';

const Projects = () => {

  // Sort latest first (2021 on top automatically)

  const [projects] = useState(
  [...projectsData].sort((a, b) => {
    const pa = a.priority ?? 999;
    const pb = b.priority ?? 999;

    if (pa !== pb) {
      return pa - pb; // lower priority number first
    }

    return b.year - a.year; // then latest year
  })
);
  const [filter, setFilter] = useState('all');

  const filteredProjects =
    filter === 'all'
      ? projects
      : projects.filter((p) => p.status === filter);

  return (
    <div className="min-h-screen pt-20" data-testid="projects-page">
      <Seo {...PAGES.projects} />

      {/* HERO */}
      <section className="py-24 bg-slate-950" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tighter text-white mb-6 text-center"
            data-testid="projects-title"
          >
            Our <span className="text-ocean-500">Projects</span>
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto text-center"
          >
            Hydro power control systems engineered and commissioned across India
          </motion.p>
        </div>
      </section>

      {/* FILTER */}
      <section className="py-12 bg-slate-900" data-testid="projects-filter">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3 justify-center">

            <button
              onClick={() => setFilter('all')}
              className={`px-6 py-3 font-medium uppercase tracking-widest text-xs transition-all ${
                filter === 'all'
                  ? 'bg-ocean-500 text-white'
                  : 'border border-white/20 text-slate-400 hover:border-white hover:text-white'
              }`}
            >
              All Projects
            </button>

            <button
              onClick={() => setFilter('completed')}
              className={`px-6 py-3 font-medium uppercase tracking-widest text-xs transition-all ${
                filter === 'completed'
                  ? 'bg-ocean-500 text-white'
                  : 'border border-white/20 text-slate-400 hover:border-white hover:text-white'
              }`}
            >
              Completed
            </button>

            <button
              onClick={() => setFilter('ongoing')}
              className={`px-6 py-3 font-medium uppercase tracking-widest text-xs transition-all ${
                filter === 'ongoing'
                  ? 'bg-ocean-500 text-white'
                  : 'border border-white/20 text-slate-400 hover:border-white hover:text-white'
              }`}
            >
              Ongoing
            </button>

          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-12 pb-24 bg-slate-950" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-lg">
                No projects found.
              </p>
            </div>
          ) : (

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

              {filteredProjects.map((project) => (

                <div
                  key={project.project_id}
                  className="bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 shadow-md rounded-lg overflow-hidden hover:shadow-lg transition-shadow group"
                >

                  {/* IMAGE */}
                  <div className="aspect-[4/3] bg-white flex items-center justify-center">
                    <img
                      src={project.image_url}
                      alt={project.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* CONTENT */}
                  <div className="p-6">

                    <h3 className="font-heading text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2 group-hover:text-ocean-500 transition-colors">
                      {project.name}
                    </h3>

                    <div className="space-y-2 mb-4">

                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <MapPin size={14} className="text-ocean-500" />
                        {project.location}
                      </div>

                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Zap size={14} className="text-ocean-500" />
                        {project.capacity}
                      </div>

                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar size={14} className="text-ocean-500" />
                        {project.year}
                      </div>

                    </div>

                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-3">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.scope?.map((item, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-300 uppercase tracking-wider"
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                  </div>
                </div>

              ))}

            </div>

          )}
        </div>
      </section>

    </div>
  );
};

export default Projects;